/**
 * Batch Hooks
 *
 * TanStack Query hooks for batch operations in the Production App.
 * Follows patterns established in useOrderQueue.ts.
 */

import { trpc } from '@/lib/trpc';
import type { BatchStatus } from '../types';
import type { OrderQueueApiFilters } from './useOrderQueue';

/**
 * Create a new production batch
 *
 * On success:
 * - Invalidates orderQueue (orders moved to IN_BATCH)
 * - Invalidates batches list
 * - Invalidates queue counts
 *
 * Never retried. The App-wide mutation default retries once on a non-4xx
 * failure (App.tsx), and a slow createBatch on a big order that dies at the
 * proxy/request timeout keeps running on the server, so an automatic retry
 * double-runs it. The operator retries by hand once the queue shows the truth.
 */
export function useCreateBatch() {
  const utils = trpc.useUtils();

  return trpc.productionOrders.createBatch.useMutation({
    retry: false,
    onSuccess: () => invalidateAfterBatchCreated(utils),
  });
}

/**
 * Everything a committed batch makes stale. Shared by createBatch's own
 * onSuccess and by `useBatchCreationStatus`, which learns of a batch from the
 * progress poll when the createBatch response itself was lost.
 */
export function invalidateAfterBatchCreated(utils: ReturnType<typeof trpc.useUtils>): void {
  void utils.productionOrders.getOrderQueue.invalidate();
  void utils.productionOrders.listBatches.invalidate();
  // Film Printing's DTF batch list reads the same batches through its own procedure.
  void utils.filmPrint.listDtfBatches.invalidate();
  void utils.productionOrders.getQueueCounts.invalidate();
  // Refresh dashboard stats immediately (don't wait for 30s polling)
  void utils.productionOrders.getDashboardStats.invalidate();
  // BLU-787: the batch just committed removed orders from the queue, so
  // both the scope resolver's per-scope/union counts and the auto-batch
  // preview it feeds are stale the moment this succeeds — a coordinator
  // who creates one batch and immediately opens the menu again would
  // otherwise read pre-batch counts.
  void utils.productionOrders.resolveBatchScopes.invalidate();
  void utils.productionOrders.getAutoBatchPreview.invalidate();
}

/**
 * List batches for a location with optional status filter
 */
export function useListBatches(
  locationId: string | undefined,
  options?: { status?: BatchStatus; limit?: number; offset?: number }
) {
  return trpc.productionOrders.listBatches.useQuery(
    {
      locationId: locationId!,
      status: options?.status,
      limit: options?.limit ?? 20,
      offset: options?.offset ?? 0,
    },
    {
      enabled: !!locationId,
      staleTime: 30_000, // 30 seconds
      gcTime: 5 * 60 * 1000, // 5 minutes
    }
  );
}

/**
 * Get a single batch by ID with optional details and progress tracking.
 *
 * @param batchId - Batch ID to fetch
 * @param options.includeDetails - Load garment_units and creator info
 * @param options.includeProgress - Load aggregated progress stats
 */
export function useBatch(
  batchId: string | undefined,
  options?: { includeDetails?: boolean; includeProgress?: boolean }
) {
  const includeDetails = options?.includeDetails ?? false;
  const includeProgress = options?.includeProgress ?? false;

  return trpc.productionOrders.getBatch.useQuery(
    {
      batchId: batchId!,
      includeDetails,
      includeProgress,
    },
    {
      enabled: !!batchId,
      staleTime: 30_000,
      gcTime: 5 * 60 * 1000,
    }
  );
}

// ============================================================================
// AUTO-BATCH HOOKS
// ============================================================================

import { useMemo } from 'react';
import type { PapReminderInfo } from '../types';

/**
 * Fetch auto-batch preview for a location
 *
 * Returns optimized batch composition with:
 * - Selected orders based on FIFO + SKU grouping
 * - Pending redos
 * - Summary statistics
 * - SKU breakdown
 * - P&P balance info
 *
 * @param maxGarments - Optional batch size override from user input
 */
export function useAutoBatchPreview(
  locationId: string | undefined,
  enabled = true,
  maxGarments?: number,
  /**
   * BLU-777: preview EXACTLY these orders (the scoped-batch flow). When present
   * the backend bypasses auto-batch candidate selection entirely — see
   * generateAutoBatchPreview's docblock for why reusing that selection would
   * misreport a ticked union.
   */
  orderIds?: string[]
) {
  return trpc.productionOrders.getAutoBatchPreview.useQuery(
    { locationId: locationId!, maxGarments, orderIds },
    {
      enabled: enabled && !!locationId,
      staleTime: 30_000, // 30 seconds - preview should be fresh
      gcTime: 5 * 60 * 1000,
    }
  );
}

/**
 * BLU-777 — resolve the org's configured batch scopes against the live queue.
 *
 * ⚠ THE COUNTS AND THE UNION COME FROM THE SERVER, ALWAYS. The queue page holds
 * one server page (30 rows by default), so a client-side union over loaded rows
 * would batch a fraction of the matches while displaying the full count — the
 * exact failure PR #1659 shipped on the tracker. Never derive scope counts or the
 * union from `currentOrders`, and never route ticked scopes through
 * `useOrderSelection` (it prunes any id not on the current page, silently).
 *
 * Runs whenever a location is selected, because the menu shows a live count
 * beside every configured scope before anything is ticked.
 *
 * BLU-787: `filters` is the operator's live queue filter, minus `search` (see
 * `useOrderFilters`'s `scopeFilters` docblock for why search is stripped
 * before it ever reaches this hook). Passing it through is what makes the
 * batch follow what the queue is showing, instead of the whole floor.
 */
export function useBatchScopes(
  locationId: string | undefined,
  scopeIds: string[],
  extraOrderIds?: string[],
  filters?: Omit<OrderQueueApiFilters, 'search'>
) {
  return trpc.productionOrders.resolveBatchScopes.useQuery(
    { locationId: locationId!, scopeIds, extraOrderIds, filters },
    {
      enabled: !!locationId,
      // Matches the preview's freshness: counts stale by more than this would let
      // an operator batch against a queue that has moved on.
      staleTime: 30_000,
      gcTime: 5 * 60 * 1000,
      // ⚠ KEEP THE LAST RESULT WHILE A NEW KEY RESOLVES. `scopeIds` and
      // `extraOrderIds` are IN the query key, so every tick of a scope — and
      // every row checkbox while a scope is ticked — is a brand-new key with no
      // cache entry, and React Query v5 hands back `data: undefined` for those.
      // The menu renders off this data, so without a placeholder the scope list
      // blanks to the "no batch buttons set up yet" panel under the operator's
      // cursor on every single tick. Same reason `useOrderQueue` sets it.
      placeholderData: (previousData) => previousData,
      // The resolver runs the FULL unpaginated ready-tab pipeline server-side
      // (~1,237 unbatched orders at Threadheads, and across the HK proxy at
      // Ningbo). Re-running that every time the operator alt-tabs back is real
      // load on a live floor for a number that `staleTime` already governs.
      refetchOnWindowFocus: false,
    }
  );
}

/**
 * Hook to get P&P reminder info for manual batch mode
 * Uses the auto-batch preview endpoint to get papBalance data
 *
 * @param locationId - The location to check
 * @param settings - Optional override for frequency and threshold settings
 */
export function usePapReminderInfo(
  locationId: string | undefined,
  settings?: { frequency: number; threshold: number }
) {
  const { data: preview, isLoading, error } = useAutoBatchPreview(locationId, !!locationId);

  const reminderInfo: PapReminderInfo | null = useMemo(() => {
    if (!preview || isLoading) return null;

    const frequency = settings?.frequency ?? 3;
    const threshold = settings?.threshold ?? 5;

    const shouldShow =
      preview.papBalance.batchesSinceLastPap >= frequency &&
      preview.papBalance.papOnlyWaiting >= threshold;

    // Use oldest order age from summary - already calculated by backend
    const oldestOrderAge = preview.summary.oldestOrderAge;

    return {
      papOnlyCount: preview.papBalance.papOnlyWaiting,
      oldestOrderAge,
      batchesSinceLastPap: preview.papBalance.batchesSinceLastPap,
      shouldShow,
    };
  }, [preview, isLoading, settings?.frequency, settings?.threshold]);

  return { reminderInfo, isLoading, error };
}

// ============================================================================
// BATCH LABEL HOOKS
// ============================================================================

/**
 * Hook to fetch batch labels for printing.
 *
 * Returns formatted label data for the GarmentLabel component:
 * - Item numbers calculated per order ("Item X of Y")
 * - Print status tracking (labelsPrinted, labelsPrintedAt)
 * - All garment units with required metadata
 */
export function useBatchLabels(batchId: string | undefined) {
  return trpc.productionOrders.getBatchLabels.useQuery(
    { batchId: batchId! },
    {
      enabled: !!batchId,
      staleTime: 60 * 1000, // 1 minute - labels data is fairly stable
    }
  );
}

/**
 * The batch's "Buy first" list (just-in-time blanks): its just-in-time POs
 * with their lines (no prices) and the units "Update buy list" would still
 * add. ONE query beside the label query, never on the batch list or queue.
 */
export function useBuyList(batchId: string | undefined) {
  return trpc.productionOrders.getBuyList.useQuery(
    { batchId: batchId! },
    {
      enabled: !!batchId,
      staleTime: 60 * 1000,
    }
  );
}

/**
 * "Update buy list": re-runs the batch's just-in-time sync (the same RPC
 * createBatch runs). Idempotent server-side, so never retried here: a
 * refusal is final and a second press is the operator's call.
 */
export function useSyncBuyList() {
  const utils = trpc.useUtils();

  return trpc.productionOrders.syncBuyList.useMutation({
    retry: false,
    onSuccess: (_, variables) =>
      Promise.all([
        utils.productionOrders.getBuyList.invalidate({ batchId: variables.batchId }),
        // To buy's "Buy today" reads the open just-in-time POs from the PO list.
        utils.purchaseOrders.getOrders.invalidate(),
      ]),
  });
}

/**
 * Hook to log print events.
 *
 * Logs to prod_print_events table and updates batch print status.
 * On success, invalidates batch queries to refresh print status in UI.
 */
export function useLogPrintEvent() {
  const utils = trpc.useUtils();

  return trpc.productionOrders.logPrintEvent.useMutation({
    onSuccess: (_, variables) => {
      // Invalidate batch labels to update print status
      if (variables.batchId) {
        utils.productionOrders.getBatchLabels.invalidate({ batchId: variables.batchId });
        utils.productionOrders.getBatch.invalidate({ batchId: variables.batchId });
      }
    },
  });
}

/**
 * Hook to update batch status.
 *
 * Updates batch status and invalidates related queries.
 * Status flow: OPEN → PICKING → PICKED → CATCHING → PACKING → COMPLETED
 *
 * Called when:
 * - Labels printed: OPEN → PICKING
 * - All items picked: PICKING → PICKED
 * - First item caught: PICKED → CATCHING
 * - All items caught: CATCHING → PACKING
 * - Batch complete: PACKING → COMPLETED
 */
export function useUpdateBatchStatus() {
  const utils = trpc.useUtils();

  return trpc.productionOrders.updateBatchStatus.useMutation({
    onSuccess: (_, variables) => {
      // Invalidate batch queries to update status in UI
      utils.productionOrders.getBatchLabels.invalidate({ batchId: variables.batchId });
      utils.productionOrders.getBatch.invalidate({ batchId: variables.batchId });
      utils.productionOrders.listBatches.invalidate();
      utils.filmPrint.listDtfBatches.invalidate();
    },
  });
}

// ============================================================================
// COMPLETE PICKING PHASE HOOK
// ============================================================================

/**
 * Hook to complete the picking phase for a batch.
 *
 * Bulk-updates LABEL_PRINTED garments to PICKED (for "none" verification mode)
 * and recalculates batch status to PICKED.
 *
 * Called when picker clicks "Complete Picking" / "Hand Off to Printing".
 */
export function useCompletePickingPhase() {
  const utils = trpc.useUtils();

  return trpc.productionOrders.completePickingPhase.useMutation({
    onSuccess: () => {
      // Invalidate batch queries to refresh status
      utils.productionOrders.getBatch.invalidate();
      utils.productionOrders.listBatches.invalidate();
      utils.filmPrint.listDtfBatches.invalidate();
    },
  });
}

// ============================================================================
// INVENTORY ERROR HOOKS
// ============================================================================

/**
 * Hook to report an inventory error during picking.
 *
 * Marks affected garment units as INVENTORY_ERROR.
 * On success, invalidates batch queries to refresh the UI.
 */
export function useReportInventoryError() {
  const utils = trpc.useUtils();

  return trpc.productionOrders.reportInventoryError.useMutation({
    onSuccess: (_, variables) => {
      // Invalidate batch labels to refresh the picklist
      utils.productionOrders.getBatchLabels.invalidate({ batchId: variables.batchId });
      utils.productionOrders.getBatch.invalidate({ batchId: variables.batchId });
      utils.productionOrders.listBatches.invalidate();
      utils.filmPrint.listDtfBatches.invalidate();
    },
  });
}

/**
 * Hook to report a picking issue (defective or unavailable).
 *
 * Defective: stock decremented, picklist unchanged.
 * Unavailable: items removed from picklist (existing INVENTORY_ERROR flow).
 */
export function useReportPickingIssue() {
  const utils = trpc.useUtils();

  return trpc.productionOrders.reportPickingIssue.useMutation({
    onSuccess: (_, variables) => {
      // Invalidate batch queries to refresh picklist
      utils.productionOrders.getBatchLabels.invalidate({ batchId: variables.batchId });
      utils.productionOrders.getBatch.invalidate({ batchId: variables.batchId });
      utils.productionOrders.listBatches.invalidate();
      utils.filmPrint.listDtfBatches.invalidate();
    },
  });
}
